import { type Dispatch, type SetStateAction, useRef } from 'react'

import useNotification, {
  type Notification as NotificationInterface,
} from 'commons/customHooks/useNotifications'
import useOnClickOutside from 'commons/customHooks/useOnClickOutside'
import { formatSendDate } from 'commons/utils/date'
import { Icon } from 'components'
import { URL_CONST } from 'constants/urls'
import { useLocation, useNavigate } from 'react-router-dom'
import { theme } from 'styles/theme/hav-theme'

import * as S from './styles'

export type NotificationType = 'success' | 'warning' | 'information' | 'fail' | 'error'

export enum Source {
  VOLUME_EXTRA = 'VOLUME_EXTRA',
  INTEGRACAO_EDI = 'INTEGRACAO_EDI',
  APROVACAO_ND = 'APROVACAO_ND',
  REPROVACAO_ND = 'REPROVACAO_ND',
  ANALISE = 'ANALISE',
  SOLICITACAO = 'SOLICITACAO',
  JUSTIFICAR_ND = 'JUSTIFICAR_ND',
  ND_EXPIRADA = 'ND_EXPIRADA',
  RETORNO_SAP = 'RETORNO_SAP',
  RETORNO_SAP_ERR = 'RETORNO_SAP_ERR',
  RET_SAP_YPOP = 'RET_SAP_YPOP',
}

type ComponentType = {
  setOpen: Dispatch<SetStateAction<boolean>>
  notifButtonRef?: React.RefObject<HTMLDivElement | null>
}

interface NotificationProps {
  source: Source
  title: string
  subtitle?: string
  message: string
  notes: string
  type: NotificationType
  read: boolean
  create_date: string
  handleClose: () => void
}

const Notification = ({
  create_date,
  message,
  read,
  source,
  title,
  subtitle,
  notes,
  type,
  handleClose,
}: NotificationProps) => {
  const navigate = useNavigate()
  const location = useLocation()

  const orderSources = [Source.VOLUME_EXTRA, Source.INTEGRACAO_EDI]
  const backofficeSources = [Source.ANALISE, Source.SOLICITACAO]
  const ovSources = [
    Source.JUSTIFICAR_ND,
    Source.ND_EXPIRADA,
    Source.RETORNO_SAP,
    Source.RETORNO_SAP_ERR,
    Source.RET_SAP_YPOP,
  ]

  const navToSource = (source: Source, id: string) => {
    if (!id) return

    const getRoute = (source: Source) => {
      if (orderSources.includes(source)) return URL_CONST.MANAGE_ORDER
      if (backofficeSources.includes(source)) return URL_CONST.BACKOFFICE
      if (ovSources.includes(source)) return URL_CONST.MANAGE_OV
      return URL_CONST.ROOT
    }

    const navRoute =
      source === Source.JUSTIFICAR_ND
        ? `${getRoute(source)}/${id}/justificar`
        : `${getRoute(source)}/${id}`

    if (location.pathname === navRoute) {
      navigate(0)
    } else {
      navigate(navRoute)
    }
    handleClose()
  }

  const parseBold = (text: string) => {
    const parts = text.split(/(<strong>.*?<\/strong>)/g)
    return parts.map((part, idx) => {
      if (part.includes('<strong>')) {
        return <strong key={idx}>{part.replace(/<\/?strong>/g, '')}</strong>
      } else {
        return part
      }
    })
  }

  return (
    <S.NotificationContainer
      hasNotes={Boolean(notes)}
      onClick={() => navToSource(source, notes)} // NOTE: Notes is the ID of ORDER or OV
    >
      <S.NotificationRow>
        <S.IconBox read={!read} type={type}>
          {type === 'success' && (
            <Icon color={theme.colors.alert.success.darker} icon="task-alt" size="15" />
          )}
          {type === 'warning' && (
            <Icon color={theme.colors.alert.warning[50]} icon="report" size="15" />
          )}
          {type === 'information' && (
            <Icon color={theme.colors.alert.info[40]} icon="info" size="15" />
          )}
          {['fail', 'error'].includes(type) && (
            <Icon color={theme.colors.alert.danger.darker} icon="cancel" size="15" />
          )}
        </S.IconBox>
        <S.TitleContainer>
          <S.NotificationTitleRow>
            <S.TitleNotif>{title}</S.TitleNotif>
            <S.SubTitleNotif>{formatSendDate(create_date)}</S.SubTitleNotif>
          </S.NotificationTitleRow>
          {subtitle && <S.SubTitleNotif>{subtitle}</S.SubTitleNotif>}
        </S.TitleContainer>
      </S.NotificationRow>
      <S.ContentNotif>{parseBold(message)}</S.ContentNotif>
    </S.NotificationContainer>
  )
}

export const Notifications: React.FC<ComponentType> = ({ setOpen, notifButtonRef }) => {
  const { notificationsData, processUnreadNotifications } = useNotification()
  const notRef = useRef<HTMLDivElement>(null)

  const handleNotificationClose = () => {
    setOpen(false)
    processUnreadNotifications()
  }
  useOnClickOutside(notRef, handleNotificationClose, notifButtonRef)

  const handleNotifSource = (notif: NotificationInterface) => {
    if (notif.source === Source.APROVACAO_ND)
      return {
        notes: notif.notes,
        title: 'Negociação diferenciada aprovada',
        type: 'success' as NotificationType,
      }
    if (notif.source === Source.REPROVACAO_ND)
      return {
        notes: notif.notes,
        title: 'Negociação diferenciada reprovada',
        type: 'fail' as NotificationType,
      }
    if ([Source.ANALISE, Source.SOLICITACAO].includes(notif.source))
      return {
        notes: 'alcadas',
        title: notif.title,
        type: notif.type,
      }
    return { notes: notif.notes, title: notif.title, type: notif.type }
  }

  return (
    <S.WrapContainerStyled ref={notRef}>
      <S.Title>Notificações</S.Title>

      {notificationsData?.length ? (
        <S.NotificationsList>
          {notificationsData?.map((not) => {
            const { title, type, notes } = handleNotifSource(not)
            return (
              <Notification
                create_date={not.create_date}
                handleClose={handleNotificationClose}
                key={not.notificationId}
                message={not.message}
                notes={notes}
                read={not.read}
                source={not.source}
                subtitle={not.subtitle}
                title={title}
                type={type}
              />
            )
          })}
        </S.NotificationsList>
      ) : (
        <S.Content>Nenhuma notificação recebida.</S.Content>
      )}
    </S.WrapContainerStyled>
  )
}
